import { memo, useCallback, useState } from 'react'

import { ControlPointDuplicate } from '@mui/icons-material'
import { ListItemIcon, Typography, Divider, MenuItem, Skeleton } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { ActionMenu, AnimatedButton } from '@shared/components/ContextMenu'
import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { useManagedStoreCreateMutation, useManagedStoreDeleteMutation } from 'hooks/mutations/content'
import { managedStoresOverviewRoute } from 'pages/NWPlatform/ContentStores/ManagedStores'
import { managedStoreRoute } from 'pages/NWPlatform/ContentStores/ManagedStores/ManagedStore'
import { managedStoreQueryKeys } from 'services/queryKeys'

const ActionsMenuRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { managedStoreId } = managedStoreRoute.useParams()
  const navigate = useNavigate({ from: managedStoreRoute.fullPath })

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()

  const managedStoreQuery = useQuery(managedStoreQueryKeys.detail({ storeId: managedStoreId }))
  const { data: managedStore } = managedStoreQuery

  const { mutate: createManagedStore, ...managedStoreCreateMutation } = useManagedStoreCreateMutation()
  const { mutate: deleteManagedStore } = useManagedStoreDeleteMutation()

  const handleDeletion = useCallback(() => {
    if (!managedStore) return

    setAnchorEl(undefined)

    deleteManagedStore(
      { storeId: managedStore.id },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:Integration.nameDelete', { name: managedStore.name }), { variant: 'success' })

          setTimeout(() => {
            navigate({ to: managedStoresOverviewRoute.to, replace: true })
          }, 300)
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [deleteManagedStore, enqueueSnackbar, managedStore, navigate, t])

  const handleDuplicateManagedStore = useCallback(() => {
    if (!managedStore) return

    setAnchorEl(undefined)

    createManagedStore(
      { contentManagedStoreInsert: { ...managedStore, name: `${managedStore.name} (${t('duplicate')})` } },
      {
        onSuccess: () => {
          enqueueSnackbar(t('duplicationSuccess'), { variant: 'success' })
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [managedStore, createManagedStore, enqueueSnackbar, t])

  return (
    <>
      <AnimatedButton
        open={!!anchorEl}
        IconButtonProps={{
          onClick: (e) => setAnchorEl(e.currentTarget),
          color: 'inherit',
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(undefined)
        }}
        title={managedStore?.name}
        entity={t('entity:content.managedStore', { count: 1 })}
      >
        <MenuItem onClick={handleDuplicateManagedStore}>
          <ListItemIcon>
            <ControlPointDuplicate />
          </ListItemIcon>
          <Typography sx={{ width: '100%' }}>
            {managedStoreCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
          </Typography>
        </MenuItem>

        <MenuItemCopyId onSuccess={() => setAnchorEl(undefined)} id={managedStoreId} />

        <Divider sx={{ my: 1 }} component="li" />

        <MenuItemDelete
          staticLabel={t('domain:Integration.deleteManagedStore', { count: 1 })}
          onConfirm={handleDeletion}
        />
      </ActionMenu>
    </>
  )
}

export const ActionsMenu = memo(ActionsMenuRaw)
